<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import type { DimensionnementPvAnalysis } from "@modules/pv/types/dimensionnement-pv.types"
import {
    applyHomeysStandardTraceHover,
    buildHomeysStandardPlotlyLayout,
} from "@modules/data/utils/plotly"
import { useChartExport } from "@modules/pv/composables/useChartExport"

/** Couleurs distinctes par scénario (légende + marqueurs Plotly). */
const SCENARIO_COLORS = [
    "#2563eb",
    "#ea580c",
    "#16a34a",
    "#9333ea",
    "#dc2626",
    "#0891b2",
    "#ca8a04",
    "#4f46e5",
]

type ScenarioInput = DimensionnementPvAnalysis & { color?: string }

const props = withDefaults(
    defineProps<{
        /** Analyses dimensionnement (historique) — `color` optionnelle par ligne. */
        scenarios: ScenarioInput[]
    }>(),
    {
        scenarios: () => [],
    }
)

const { t } = useI18n({
    messages: {
        fr: {
            cardTitle:
                "Comparaison des taux d’autoconsommation • Historique des études",
            errorNoVisible:
                "Veuillez laisser au moins une étude visible pour afficher le graphique.",
            legendHint: "Cliquer sur une étude pour masquer / afficher.",
            axisX: "Puissance installée (kWc)",
            axisY: "Taux d’autoconsommation (%)",
            exportExcel: "Télécharger Excel",
            colStudy: "Étude",
        },
    },
})

const { downloadRowsAsExcel } = useChartExport()

/** Identifiants `_id` des scénarios masqués dans le graphique. */
const hiddenScenarioIds = ref<string[]>([])

watch(
    () => props.scenarios.map((s) => s._id).join(","),
    () => {
        hiddenScenarioIds.value = []
    }
)

function getPeakPowerKw(scenario: DimensionnementPvAnalysis): number {
    const list = scenario.inputs?.list_params_pv ?? []
    return list.reduce((sum, seg) => sum + (Number(seg.peakpower) || 0), 0)
}

function getRatioPercent(scenario: DimensionnementPvAnalysis): number | null {
    const ext = scenario as DimensionnementPvAnalysis & {
        data?: { ratio_autoconso?: { value?: number } }
    }
    const v = ext.data?.ratio_autoconso?.value
    return typeof v === "number" && !Number.isNaN(v) ? v : null
}

function getScenarioColor(scenario: ScenarioInput, index: number): string {
    return (
        scenario.color ??
        SCENARIO_COLORS[index % SCENARIO_COLORS.length] ??
        "#64748b"
    )
}

const getLegendItems = computed(() => {
    return props.scenarios.map((scenario, index) => ({
        id: scenario._id,
        name:
            typeof scenario.name_analyse === "string" &&
            scenario.name_analyse.length > 0
                ? scenario.name_analyse
                : scenario._id,
        color: getScenarioColor(scenario, index),
        visible: !hiddenScenarioIds.value.includes(scenario._id),
    }))
})

function onToggleLegend(id: string): void {
    const set = new Set(hiddenScenarioIds.value)
    if (set.has(id)) {
        set.delete(id)
    } else {
        set.add(id)
    }
    hiddenScenarioIds.value = [...set]
}

function onClickDownloadExcel(): void {
    const visible = props.scenarios.filter(
        (s) => !hiddenScenarioIds.value.includes(s._id)
    )
    const rows: unknown[][] = [[t("colStudy"), t("axisX"), t("axisY")]]
    for (const scenario of visible) {
        const power = getPeakPowerKw(scenario)
        const ratio = getRatioPercent(scenario)
        if (!(power > 0) || ratio == null) continue
        const label =
            typeof scenario.name_analyse === "string" &&
            scenario.name_analyse.length > 0
                ? scenario.name_analyse
                : scenario._id
        rows.push([label, power, ratio])
    }
    if (rows.length <= 1) return
    downloadRowsAsExcel(rows, {
        chartSlug: "comparateur-taux-autoconso",
        sheetName: "Taux autoconso",
    })
}

const getPlotlyGraph = computed(() => {
    const visible = props.scenarios.filter(
        (s) => !hiddenScenarioIds.value.includes(s._id)
    )
    const withData = visible
        .map((s) => ({
            scenario: s,
            x: getPeakPowerKw(s),
            y: getRatioPercent(s),
        }))
        .filter((row) => row.x > 0 && row.y !== null) as {
        scenario: ScenarioInput
        x: number
        y: number
    }[]

    if (withData.length === 0) {
        return null
    }

    withData.sort((a, b) => a.x - b.x)

    const labels = withData.map((row) =>
        typeof row.scenario.name_analyse === "string" &&
        row.scenario.name_analyse.length > 0
            ? row.scenario.name_analyse
            : row.scenario._id
    )
    const markerColors = withData.map((row) =>
        getScenarioColor(row.scenario, props.scenarios.indexOf(row.scenario))
    )
    const xs = withData.map((row) => row.x)
    const ys = withData.map((row) => row.y)

    /** Une seule trace : courbe reliant les points (abscisses triées) + marqueurs colorés. */
    const lineTrace = {
        type: "scatter" as const,
        mode: "lines+markers" as const,
        name: "taux_autoconso",
        x: xs,
        y: ys,
        line: {
            color: "#94a3b8",
            width: 2,
            shape: "linear" as const,
        },
        marker: {
            size: 12,
            color: markerColors,
            line: { width: 1, color: "#ffffff" },
        },
        text: labels,
        hovertemplate:
            "<b>%{text}</b><br>" +
            `${t("axisX")}: %{x:.2f} kWc<br>` +
            `${t("axisY")}: %{y:.1f} %<extra></extra>`,
    }

    const axisYLabel = t("axisY")
    const data = applyHomeysStandardTraceHover([lineTrace], {
        yAxisLabel: axisYLabel,
    })

    const layout = buildHomeysStandardPlotlyLayout(
        {
            autosize: true,
            showlegend: false,
            xaxis: { automargin: true },
            yaxis: { automargin: true, rangemode: "tozero" as const },
        },
        {
            x_label: t("axisX"),
            y_label: axisYLabel,
            name: "pv_taux_autoconso_historique",
        }
    )

    return { data, layout }
})
</script>

<template>
    <BaseTitledCard
        class="pv-taux-autoconso-card shadow-sm border border-surface-200 overflow-hidden"
        content-class="p-4 sm:p-6"
        header-class="!text-left"
    >
        <template #header>
            <div
                class="flex flex-wrap items-center justify-between gap-2 w-full font-semibold text-surface-900"
            >
                <span>{{ t("cardTitle") }}</span>
                <BaseButton
                    :label="t('exportExcel')"
                    icon="pi pi-file-excel"
                    severity="secondary"
                    text
                    size="small"
                    :disabled="!getPlotlyGraph"
                    @click="onClickDownloadExcel"
                />
            </div>
        </template>
        <template #content>
            <div class="flex flex-col gap-4">
                <p class="text-sm text-surface-600">{{ t("legendHint") }}</p>
                <div
                    class="pv-taux-autoconso__legend flex flex-wrap gap-3"
                    role="list"
                >
                    <button
                        v-for="item in getLegendItems"
                        :key="item.id"
                        type="button"
                        class="inline-flex items-center gap-2 rounded-md border border-surface-200 px-3 py-1.5 text-sm text-surface-800 transition hover:bg-surface-50"
                        :class="
                            item.visible
                                ? 'opacity-100'
                                : 'opacity-40 line-through'
                        "
                        role="listitem"
                        @click="onToggleLegend(item.id)"
                    >
                        <span
                            class="h-3 w-3 shrink-0 rounded-full border border-surface-300"
                            :style="{ backgroundColor: item.color }"
                            aria-hidden="true"
                        />
                        <span class="truncate max-w-[14rem]">{{
                            item.name
                        }}</span>
                    </button>
                </div>
                <div
                    v-if="getPlotlyGraph"
                    class="relative h-[28rem] w-full min-h-[20rem] overflow-hidden"
                >
                    <PlotlyChart :graph="getPlotlyGraph" />
                </div>
                <BaseMessage v-else severity="warn" :closable="false">
                    {{ t("errorNoVisible") }}
                </BaseMessage>
            </div>
        </template>
    </BaseTitledCard>
</template>
